// DashboardSwitcher — horizontal carousel pinned at the top of the wall
// surface, below the topbar. Mac Mission-Control feel: collapsed it's a
// 12px hairline w/ a centered chevron; hovering or pinning expands it.
//
// State:
//   - `pinned` is persisted in localStorage under
//     `reb.ui.prefs.switcherPinned` so the operator's preference rides
//     across page reloads.
//   - The list of dashboards is owned by the parent (so the wall can
//     show the active dash without re-fetching). The switcher is a
//     pure presentation component with a couple of click callbacks.
//   - Search filters the rail in-place. We don't navigate on filter —
//     the operator narrows the rail then clicks a card.
//
// Each `dash-card` renders a tiny grid preview computed from
// `dashboard.tiles.length` (kind-3 / kind-4 / kind-9 / kind-16 buckets,
// matching the mockup).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Dashboard } from "@/types/dashboard";

const PIN_KEY = "reb.ui.prefs.switcherPinned";

function readSwitcherPinned(): boolean {
  try {
    return window.localStorage.getItem(PIN_KEY) === "1";
  } catch {
    return false;
  }
}

function writeSwitcherPinned(pinned: boolean): void {
  try {
    window.localStorage.setItem(PIN_KEY, pinned ? "1" : "0");
  } catch {
    /* localStorage unavailable — non-fatal. */
  }
}

function previewKindFor(n: number): "kind-3" | "kind-4" | "kind-9" | "kind-16" {
  if (n <= 3) return "kind-3";
  if (n <= 4) return "kind-4";
  if (n <= 9) return "kind-9";
  return "kind-16";
}

function previewCellCount(
  kind: "kind-3" | "kind-4" | "kind-9" | "kind-16",
): number {
  switch (kind) {
    case "kind-3":
      // 3-tile preview: 1 hero + 3 small = 4 cells visible.
      return 4;
    case "kind-4":
      return 4;
    case "kind-9":
      return 9;
    case "kind-16":
      return 16;
  }
}

export interface DashboardSwitcherProps {
  /** Caller-owned list, sorted however the caller wants (usually most-recent-updated). */
  dashboards: Dashboard[];
  /** The dashboard whose wall is currently rendered. null on overview. */
  activeId: string | null;
  /** Click on a dashboard card → navigate to its wall. */
  onSelect: (dashboardId: string) => void;
  /** Click on the trailing "+ New dashboard" button. */
  onCreate: () => void;
}

export function DashboardSwitcher({
  dashboards,
  activeId,
  onSelect,
  onCreate,
}: DashboardSwitcherProps): JSX.Element {
  const [pinned, setPinned] = useState<boolean>(() => readSwitcherPinned());
  const [search, setSearch] = useState<string>("");
  // Mark mount complete so the SSR/initial-paint pin state lines up with
  // the persisted value before any user interaction.
  const mountedRef = useRef<boolean>(false);
  useEffect(() => {
    mountedRef.current = true;
  }, []);

  const handleTogglePin = useCallback((): void => {
    setPinned((prev) => {
      const next = !prev;
      writeSwitcherPinned(next);
      return next;
    });
  }, []);

  const filtered = useMemo<Dashboard[]>(() => {
    const q = search.trim().toLowerCase();
    if (!q) return dashboards;
    return dashboards.filter((d) => d.display_name.toLowerCase().includes(q));
  }, [dashboards, search]);

  // Keyboard navigation: arrow keys move focus between dash-card buttons,
  // Enter activates. Limited to the rail itself so the search input keeps
  // its native arrow-key text-edit behavior.
  const railRef = useRef<HTMLDivElement | null>(null);
  const handleRailKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>): void => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      const root = railRef.current;
      if (!root) return;
      const cards = Array.from(
        root.querySelectorAll<HTMLButtonElement>(".v7-dash-card"),
      );
      if (cards.length === 0) return;
      const active = document.activeElement as HTMLElement | null;
      const currentIndex = active ? cards.indexOf(active as HTMLButtonElement) : -1;
      // If focus isn't on a card yet, ArrowRight goes to the first one,
      // ArrowLeft to the last (typical roving-tabindex feel).
      let nextIndex: number;
      if (currentIndex < 0) {
        nextIndex = e.key === "ArrowRight" ? 0 : cards.length - 1;
      } else {
        const delta = e.key === "ArrowRight" ? 1 : -1;
        nextIndex =
          (currentIndex + delta + cards.length) % cards.length;
      }
      e.preventDefault();
      cards[nextIndex]?.focus();
    },
    [],
  );

  const rootClass = pinned ? "v7-switcher v7-switcher--pinned" : "v7-switcher";

  return (
    <div
      className={rootClass}
      // tabIndex so keyboard users can focus the bar to expand it via
      // :focus-within when not pinned.
      tabIndex={pinned ? -1 : 0}
      aria-label="Dashboards"
    >
      <button
        type="button"
        className="v7-switcher__peek"
        title={pinned ? "Hide dashboards" : "Show dashboards"}
        onClick={handleTogglePin}
        aria-label={pinned ? "Collapse dashboards" : "Expand dashboards"}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M7 10l5 5 5-5z" />
        </svg>
      </button>
      <div className="v7-switcher__inner">
        <div
          className="v7-switcher__rail"
          role="tablist"
          aria-label="Dashboards"
          ref={railRef}
          onKeyDown={handleRailKeyDown}
        >
          {filtered.map((dash) => {
            const tileCount = dash.tiles.length;
            const kind = previewKindFor(tileCount);
            const cells = previewCellCount(kind);
            const isActive = dash.dashboard_id === activeId;
            return (
              <button
                type="button"
                key={dash.dashboard_id}
                className={`v7-dash-card ${kind}${isActive ? " is-active" : ""}`}
                title={dash.display_name}
                role="tab"
                aria-selected={isActive}
                onClick={() => onSelect(dash.dashboard_id)}
              >
                <div className="v7-dash-card__mini" aria-hidden="true">
                  {Array.from({ length: cells }).map((_, i) => (
                    <i key={i} />
                  ))}
                </div>
                <span className="v7-dash-card__name">{dash.display_name}</span>
                <span className="v7-dash-card__count">
                  {tileCount} {tileCount === 1 ? "cam" : "cams"}
                </span>
              </button>
            );
          })}
          <button
            type="button"
            className="v7-new-dash-btn"
            title="Create new dashboard"
            onClick={onCreate}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6z" />
            </svg>
            New dashboard
          </button>
        </div>
        <div className="v7-switcher__divider" aria-hidden="true" />
        <div className="v7-switcher__search">
          <span className="ico" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <path d="M15.5 14h-.8l-.3-.3a6.5 6.5 0 1 0-.7.7l.3.3v.8l5 5 1.5-1.5-5-5zm-6 0a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9z" />
            </svg>
          </span>
          <input
            type="text"
            placeholder="Search dashboards…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search dashboards"
          />
        </div>
        <button
          type="button"
          className="v7-switcher__pin"
          title={pinned ? "Collapse switcher" : "Keep switcher open"}
          aria-label={pinned ? "Collapse switcher" : "Keep switcher open"}
          aria-pressed={pinned}
          onClick={handleTogglePin}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M14 4l6 6-3 3-1-1-4 4v4l-2 2-4-4-1 1-1-1 1-1-4-4 2-2h4l4-4-1-1 3-3z" />
          </svg>
        </button>
      </div>
    </div>
  );
}
